import PropTypes from 'prop-types'
import { memo, useEffect, useMemo, useRef } from 'react'
import Button from 'components/core/Button/Button'
import classNames from 'lib/util/classNames'
import { useModalContext } from '../../provider/ModalProvider'
import styles from './Footer.module.scss'

/**
 * The `Footer`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function Footer (props) {
  const { children, options: inputOptions } = props
  const { isModalContentActive: isActive, onAction } = useModalContext()
  const options = useMemo(
    () =>
      inputOptions.map(option => ({
        action: 'close',
        label: 'Close',
        Component: Button,
        color: 'a',
        ...option
      })),
    [inputOptions]
  )
  const classNameOutput = useMemo(
    () => classNames([styles.footer, isActive && styles.active]),
    [isActive]
  )

  const ref = {
    options: useRef()
  }

  /**
   * Effect: When the `isActive` state changes and is active, set
   * the focus element on the "close" button of the modal, per aria
   * guidelines
   */
  useEffect(() => {
    if (!isActive) {
      // Not active, ignore
      return
    }

    const firstOption = ref.options.current.querySelector(
      'li button[data-action="close"]'
    )

    if (!firstOption) {
      // Defensive
      return
    }

    firstOption.focus()
  }, [isActive])

  return (
    <div className={classNameOutput}>
      <div className={styles.footerDisplay}>
        <ul ref={ref.options} className={styles.footerOptions}>
          {children}
          {options.map(({ Component, action, id, label, ...rest }, i) => (
            <li key={i}>
              <Component
                {...rest}
                data-action={action}
                size="large"
                className={styles.footerOption}
                onClick={() => onAction(action)}
              >
                {label}
              </Component>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** @type {object} */
Footer.propTypes = {
  children: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string,
      label: PropTypes.string,
      Component: PropTypes.elementType
    })
  )
}

/** @type {object} */
Footer.defaultProps = {
  children: null,
  options: [
    {
      action: 'close',
      label: 'Close',
      color: 'a',
      Component: Button
    }
  ]
}

// Memoize
export default memo(Footer)
